import type { NavigateOptions } from 'react-router-dom';

import { resolveSafeAppBackTarget } from '@/lib/smart-back';

interface InvoiceDetailNavigationState {
  invoiceBackTargets?: string[];
}

interface InvoicesListNavigationState {
  restoreInvoicesListState?: boolean;
}

const INVOICES_LIST_PATH = '/webadmin/app/invoices';
const SAFE_BACK_TARGET_PREFIXES = [
  INVOICES_LIST_PATH,
  '/webadmin/app/analytics',
  '/webadmin/app/settings/ksef',
  '/webadmin/app/payments',
] as const;

const RESTORE_INVOICES_LIST_STATE_KEY = 'restoreInvoicesListState';
const MAX_INVOICE_BACK_TARGETS = 16;

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function resolveAllowedInvoiceBackTarget(candidate: unknown): string | null {
  if (typeof candidate !== 'string') return null;
  const safeCandidate = resolveSafeAppBackTarget(candidate);
  if (!safeCandidate) return null;

  const pathname = new URL(safeCandidate, 'https://erp24.local').pathname;
  const hasAllowedPrefix = SAFE_BACK_TARGET_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return hasAllowedPrefix ? safeCandidate : null;
}

export function resolveInvoiceBackTargets(state: unknown): string[] {
  if (!isObjectRecord(state)) return [];
  const candidates = (state as InvoiceDetailNavigationState).invoiceBackTargets;
  if (!Array.isArray(candidates) || candidates.length === 0) return [];

  const targets = candidates.slice(0, MAX_INVOICE_BACK_TARGETS).map(resolveAllowedInvoiceBackTarget);
  return targets.every((target): target is string => target !== null) ? targets : [];
}

export function resolveInvoiceBackTarget(state: unknown): string | null {
  return resolveInvoiceBackTargets(state)[0] ?? null;
}

export function buildInvoiceDetailNavigationState(
  invoiceBackTarget: string,
  previousBackTargets: string[] = [],
): InvoiceDetailNavigationState {
  return {
    invoiceBackTargets: [invoiceBackTarget, ...previousBackTargets].slice(
      0,
      MAX_INVOICE_BACK_TARGETS,
    ),
  };
}

export function buildInvoicesListRestoreState(): InvoicesListNavigationState {
  return {
    restoreInvoicesListState: true,
  };
}

export function buildInvoiceBackNavigationOptions(
  target: string,
  remainingBackTargets: string[] = [],
): NavigateOptions {
  const options: NavigateOptions = { replace: true };
  try {
    const url = new URL(target, 'https://erp24.local');
    if (url.pathname === INVOICES_LIST_PATH) {
      return { ...options, state: buildInvoicesListRestoreState() };
    }
  } catch {
    // Invalid targets are rejected by resolveInvoiceBackTarget before this helper is called.
  }
  if (remainingBackTargets.length > 0) {
    return {
      ...options,
      state: { invoiceBackTargets: remainingBackTargets.slice(0, MAX_INVOICE_BACK_TARGETS) },
    };
  }
  return options;
}

export function shouldRestoreInvoicesListState(state: unknown): boolean {
  if (!isObjectRecord(state)) return false;
  return state[RESTORE_INVOICES_LIST_STATE_KEY] === true;
}

export function stripInvoicesListRestoreState(state: unknown): Record<string, unknown> | null {
  if (!isObjectRecord(state)) return null;

  if (!(RESTORE_INVOICES_LIST_STATE_KEY in state)) {
    return state;
  }

  const nextState = { ...state };
  delete nextState[RESTORE_INVOICES_LIST_STATE_KEY];
  return Object.keys(nextState).length > 0 ? nextState : null;
}
